define("shared/lib/popup-instance", ["exports", "later/services/errors"], function (_exports, _errors) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.default = _exports.PopupInstance = void 0;
  0; //eaimeta@70e063a35619d71f0,"later/services/errors"eaimeta@70e063a35619d71f

  function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }

  function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

  function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

  function _classPrivateFieldGet(receiver, privateMap) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get"); return _classApplyDescriptorGet(receiver, descriptor); }

  function _classApplyDescriptorGet(receiver, descriptor) { if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }

  function _classPrivateFieldSet(receiver, privateMap, value) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set"); _classApplyDescriptorSet(receiver, descriptor, value); return value; }

  function _classExtractFieldDescriptor(receiver, privateMap, action) { if (!privateMap.has(receiver)) { throw new TypeError("attempted to " + action + " private field on non-instance"); } return privateMap.get(receiver); }

  function _classApplyDescriptorSet(receiver, descriptor, value) { if (descriptor.set) { descriptor.set.call(receiver, value); } else { if (!descriptor.writable) { throw new TypeError("attempted to set read only private field"); } descriptor.value = value; } }

  /**
   * Check whether or not a configuration object is provided by an external management class.
   * @param config Popup window configuration object
   * @returns Popup window instance is being managed by external class
   */
  function isPopupInstanceManaged(config) {
    return Boolean(config._manager);
  }
  /**
   * PopupInstance holds reference to a popup `window` context. It also wraps functionality
   * for opening and closing the popup, sending messages to the parent `window` context,
   * and waiting for those messages asynchronously.
   *
   * To define the data type that the Popup instance will send back to the parent (if any)
   * define the `TMessageData` type parameter when creating or fetching an existing popup.
   */


  var _config = /*#__PURE__*/new WeakMap();

  var _popupWindow = /*#__PURE__*/new WeakMap();

  var _pollingTimer = /*#__PURE__*/new WeakMap();

  var _messageEventHandler = /*#__PURE__*/new WeakMap();

  var _unloadEventHandler = /*#__PURE__*/new WeakMap();

  class PopupInstance {
    constructor(args) {
      _defineProperty(this, "name", void 0);

      _classPrivateFieldInitSpec(this, _config, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _popupWindow, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _pollingTimer, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _messageEventHandler, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _unloadEventHandler, {
        writable: true,
        value: void 0
      });

      this.name = args.name;

      _classPrivateFieldSet(this, _config, args.config);
    }

    get isOpen() {
      if (!_classPrivateFieldGet(this, _popupWindow)) {
        return false;
      }

      return !_classPrivateFieldGet(this, _popupWindow).closed;
    }

    async open() {
      let args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
        style: 'tab'
      };
      // Note: determines whether or not the popup instance is shown as a tab or window.
      const target = args.style === 'window' ? '_blank window' : '_blank';

      try {
        const popupWindow = window.open(_classPrivateFieldGet(this, _config).url, target);

        if (!popupWindow) {
          throw new Error('Popup window is `null`');
        }

        _classPrivateFieldSet(this, _popupWindow, popupWindow);
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        throw new Error(`Unable to open Popup window: ${reason}`);
      }

      if (isPopupInstanceManaged(_classPrivateFieldGet(this, _config))) {
        _classPrivateFieldGet(this, _config)._manager.onOpened?.();
      }

      try {
        const data = await this.waitForMessage();
        return data ? data : {};
      } catch (error) {
        this.close();
        throw error;
      }
    }

    close() {
      if (!_classPrivateFieldGet(this, _popupWindow)) {
        if (isPopupInstanceManaged(_classPrivateFieldGet(this, _config))) {
          _classPrivateFieldGet(this, _config)._manager.onError("Popup cannot be closed because it isn't open.", _errors.ErrorSeverity.Warning);
        }

        return;
      }

      _classPrivateFieldGet(this, _popupWindow).close();

      this.teardown();

      if (isPopupInstanceManaged(_classPrivateFieldGet(this, _config))) {
        _classPrivateFieldGet(this, _config)._manager.onClosed?.();
      }
    }

    sendMessage(data) {
      try {
        _classPrivateFieldGet(this, _popupWindow)?.opener.postMessage(data, window.location.origin);

        if (isPopupInstanceManaged(_classPrivateFieldGet(this, _config))) {
          _classPrivateFieldGet(this, _config)._manager.onMessageSent?.(data);
        }
      } catch (error) {
        if (isPopupInstanceManaged(_classPrivateFieldGet(this, _config))) {
          _classPrivateFieldGet(this, _config)._manager.onError(`An error occurred while attempting to send a message to popup: ${error}`);
        }
      }
    }

    async waitForMessage() {
      try {
        return await new Promise((resolve, reject) => {
          // Note: we pass in the promise resolve/reject callbacks here so that the listener
          // and polling functions can properly handle resolving/rejecting the promise.
          this.setup({
            resolve,
            reject
          });
        }).then(data => {
          this.teardown();
          return data;
        });
      } catch (error) {
        this.teardown();
        throw error;
      }
    }

    setup(args) {
      this.setupMessageEventHandler(args.resolve);
      this.setupUnloadEventHandler(args.reject);
      this.setPollingInterval(args.reject);
    }

    teardown() {
      this.teardownMessageEventHandler();
      this.teardownUnloadEventHandler();
      this.clearPollingInterval();
    }

    setupMessageEventHandler(resolve) {
      if (_classPrivateFieldGet(this, _messageEventHandler)) {
        this.teardownMessageEventHandler();
      }

      _classPrivateFieldSet(this, _messageEventHandler, event => {
        // Note: only accept messages from our own origin (i.e. *.later.com)
        if (event.origin !== window.location.origin) {
          return;
        }

        resolve(event.data);

        if (isPopupInstanceManaged(_classPrivateFieldGet(this, _config))) {
          _classPrivateFieldGet(this, _config)._manager.onMessageReceived?.(event);
        }
      });

      window.addEventListener(PopupInstance.WINDOW_MESSAGE_EVENT_ID, _classPrivateFieldGet(this, _messageEventHandler));
    }

    teardownMessageEventHandler() {
      if (!_classPrivateFieldGet(this, _messageEventHandler)) {
        return;
      }

      window.removeEventListener(PopupInstance.WINDOW_MESSAGE_EVENT_ID, _classPrivateFieldGet(this, _messageEventHandler));

      _classPrivateFieldSet(this, _messageEventHandler, undefined);
    }

    setupUnloadEventHandler(reject) {
      _classPrivateFieldSet(this, _unloadEventHandler, () => {
        reject(new Error(`Popup "${this.name}" has been closed or replaced.`));
      });

      _classPrivateFieldGet(this, _popupWindow)?.addEventListener(PopupInstance.WINDOW_UNLOAD_EVENT_ID, _classPrivateFieldGet(this, _unloadEventHandler));
    }

    teardownUnloadEventHandler() {
      if (!_classPrivateFieldGet(this, _unloadEventHandler)) {
        return;
      }

      window.removeEventListener(PopupInstance.WINDOW_UNLOAD_EVENT_ID, _classPrivateFieldGet(this, _unloadEventHandler));

      _classPrivateFieldSet(this, _unloadEventHandler, undefined);
    }

    setPollingInterval(reject) {
      const windowPollingMs = 100; // Note: stop any existing polling intervals before creating another

      this.clearPollingInterval(); // Note: create a polling interval to check on the state of the popup window,
      // allowing us to react in the event of a user-event (i.e. manual closing)

      _classPrivateFieldSet(this, _pollingTimer, setInterval(() => {
        if (this.isOpen) {
          return;
        }

        const pollingError = new Error(`Popup window "${this.name}" has been closed manually.`);
        this.close();
        reject(pollingError);
      }, windowPollingMs));
    }

    clearPollingInterval() {
      if (!_classPrivateFieldGet(this, _pollingTimer)) {
        return;
      }

      clearInterval(_classPrivateFieldGet(this, _pollingTimer));
    }

  }

  _exports.PopupInstance = PopupInstance;

  _defineProperty(PopupInstance, "WINDOW_MESSAGE_EVENT_ID", 'message');

  _defineProperty(PopupInstance, "WINDOW_UNLOAD_EVENT_ID", 'beforeunload');

  var _default = PopupInstance;
  _exports.default = _default;
});